import React, { useEffect } from "react";
import moment from "moment";
import Pagination from "../../../../../components/common/Pagination";
import { TableCheckout, reviewAdd } from "../../../../../api/user/restaurants";
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "../../../../../hooks/useTranslation";

function Orders({
  isLoading,
  isError,
  error,
  reservations = [],
  pagination,
  setPage,
  afterReviewGo,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState(null);
  const [rating, setRating] = React.useState(0);
  const [comment, setComment] = React.useState("");
  const QueryClient = useQueryClient();

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const updateMutation = useMutation({
    mutationFn: TableCheckout,
    onSuccess: ({ url }) => {
      if (url) {
        window.open(url, "_blank");
      } else {
        toast.error(t("tableorders").checkoutUrlNotFound);
      }
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("tableorders").failedToGetCheckoutUrl;
      toast.error(errorMessage);
    },
  });

  const reviewMutation = useMutation({
    mutationFn: reviewAdd,
    onSuccess: () => {
      QueryClient.invalidateQueries("user-orders");
      toast.success(t("tableorders").reviewSubmitted);
      setShowReviewModal(false);
      setSelectedOrder(null);
      setRating(0);
      setComment("");
      afterReviewGo();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message || t("tableorders").failedToSubmitReview
      );
    },
  });

  return (
    <div className="overflow-x-auto md:mt-[30px] mt-[30px]">
      <table className="min-w-full">
        <thead className="bg-[#FFFAF2]">
          <tr>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").orderId}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").bookingDate}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").tableNo}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").items}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").tableCode}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").paymentDetail}
            </th>
            <th className="text-left text-lg font-medium text-[#000000] py-[12px] px-[16px]">
              {t("tableorders").trackOrder}
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="7" className="text-center py-8">
                <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </td>
            </tr>
          ) : isError ? (
            <tr>
              <td colSpan="7" className="text-center py-8 text-red-500">
                {error?.message || "Something went wrong."}
              </td>
            </tr>
          ) : reservations?.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center py-8 text-gray-500">
               
              {t("tableorders").noOrder}
              </td>
            </tr>
          ) : (
            reservations.map((reservation) => (
              <tr key={reservation._id} className="border-t border-[#E4E4E4]">
                <td className="py-[12px] px-[16px] text-[17px] font-medium text-gray-600">
                  {reservation?.orderId}
                </td>
                <td className="py-[12px] px-[16px] text-[17px] font-medium text-gray-600">
                  {reservation?.reservationId?.bookingDate 
                    ? moment(reservation.reservationId.bookingDate).format("DD MMM YYYY")
                    : moment(reservation.createdAt).format("DD MMM YYYY")
                  }
                </td>
                <td className="py-[12px] px-[16px] text-[17px] font-medium text-gray-600">
                  {reservation?.reservationId?.tableNo || reservation?.tableNumber || "-"}
                </td>
                <td className="py-[12px] px-[16px] text-[17px] font-medium text-gray-600">
                  
                  {reservation?.items?.map((item, index) => (
                    <div key={index}>
                      {item.quantity} x {item.itemId?.itemName}
                    </div>
                  ))}

{reservation?.deliveryType && (
                    <div className="mb-2">
                      <span className="font-medium">{t("cart").deliveryOptions.title}: </span>
                      {reservation.deliveryType}
                    </div>
                  )}
                  {reservation?.deliveryAddress && (
                    <div className="mb-2">
                      <span className="font-medium">{t("cart").deliveryOptions.delivery.address.label}: </span>
                      {reservation.deliveryAddress}
                    </div>
                  )}
                </td>
                <td className="py-[12px] px-[16px] text-[17px] font-medium text-gray-600">
                  {reservation?.reservationId?.tableCode || "-"}
                </td>
                <td className="py-[12px] px-[16px] text-[16px] text-black align-top">
                  <div className="grid gap-y-1 gap-x-2 text-[15px] leading-relaxed">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").transactionId}
                      </span>
                      <span className="text-ellipsis overflow-hidden">
                        {reservation.paymentDetails?.transactionId || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").subtotal}
                      </span>
                      <span>₾{reservation.subtotal?.toFixed(2) || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").discount}
                      </span>
                      <span>
                        ₾{reservation.discountAmount?.toFixed(2) || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").tokenCharges}
                      </span>
                      <span>
                        ₾{reservation.tokenCharges?.toFixed(2) || "0"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").totalAmount}
                      </span>
                      <span>₾{reservation.totalAmount?.toFixed(2) || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").paymentGateway}
                      </span>
                      <span>
                        {reservation.paymentDetails?.paymentGateway || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">
                        {t("tableorders").paidAt}
                      </span>
                      <span>
                        {reservation.paymentDetails?.paidAt
                          ? moment(reservation.paymentDetails.paidAt).format(
                              "DD MMM YYYY, h:mm A"
                            )
                          : "-"}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="py-[12px] px-[16px] text-[17px] font-medium">
                  {
                    (reservation?.orderStatus !== "pending" && reservation?.orderStatus !== "placed" && reservation?.orderStatus !== "completed") && (
                      <span
                        onClick={() =>
                          navigate("/trackorder", {
                            state: { orderId: reservation?._id },
                          })
                        }
                        className="
      inline-block px-4 py-1 rounded-full text-sm font-semibold
      bg-blue-100 text-blue-800
      hover:bg-blue-200 hover:text-blue-900
      cursor-pointer transition duration-200
      shadow-sm hover:shadow-md text-center
    "
                      >
                        {t("tableorders").trackOrder}
                      </span>
                    )}

                  {reservation?.hasReview === false &&
                    reservation?.orderStatus === "completed" && (
                      <>
                        <span
                          onClick={() => {
                            setSelectedOrder(reservation);
                            setShowReviewModal(true);
                          }}
                          className="inline-block mt-2 px-4 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-900 cursor-pointer transition duration-200 shadow-sm hover:shadow-md"
                        >
                          {t("tableorders").addReview}
                        </span>
                      </>
                    )}

                  {reservation?.hasReview === true &&
                    reservation?.orderStatus === "completed" && (
                      <span className="inline-block mt-2 px-4 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800 hover:bg-green-200 hover:text-green-900 cursor-pointer transition duration-200 shadow-sm hover:shadow-md">
                        {t("tableorders").orderCompleted}
                      </span>
                    )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {pagination?.totalPages > 1 && (
        <Pagination
          page={pagination.currentPage}
          totalPages={pagination.totalPages}
          setPage={setPage}
        />
      )}

      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-lg">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              {t("tableorders").writeReview}
            </h2>

            {/* Star Rating */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("tableorders").rating}
              </label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => {
                  const isFilled = star <= rating;
                  return (
                    <svg
                      key={star}
                      onClick={() => setRating(star)}
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill={isFilled ? "#facc15" : "#e5e7eb"}
                      stroke={isFilled ? "none" : "black"}
                      strokeWidth={isFilled ? "0" : "1"}
                      className="w-7 h-7 cursor-pointer hover:scale-110 transition-transform duration-150"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.23 3.776a1 1 0 00.95.69h3.982c.969 0 1.371 1.24.588 1.81l-3.222 2.34a1 1 0 00-.364 1.118l1.23 3.776c.3.921-.755 1.688-1.538 1.118l-3.222-2.34a1 1 0 00-1.176 0l-3.222 2.34c-.783.57-1.838-.197-1.538-1.118l1.23-3.776a1 1 0 00-.364-1.118L2.27 9.203c-.783-.57-.38-1.81.588-1.81h3.982a1 1 0 00.95-.69l1.23-3.776z" />
                    </svg>
                  );
                })}
              </div>
            </div>

            {/* Comment Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t("tableorders").comment}
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                rows="4"
                placeholder={t("tableorders").pleaseProvideBoth}
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedOrder(null);
                  setRating(0);
                  setComment("");
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
              >
                {t("tableorders").cancel}
              </button>
              <button
                onClick={() => {
                  if (!rating || !comment) {
                    toast.error(t("tableorders").pleaseProvideBoth);
                    return;
                  }
                  reviewMutation.mutate({
                    orderId: selectedOrder?._id,
                    rating,
                    comment,
                  });
                }}
                disabled={reviewMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition"
              >
                {reviewMutation.isLoading
                  ? t("tableorders").submitting
                  : t("tableorders").submit}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {/* {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Write a Review</h2>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rating
              </label>
              <select
                value={rating}
                onChange={(e) => setRating(Number(e.target.value))}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">Select rating</option>
                {[1, 2, 3, 4, 5].map((star) => (
                  <option key={star} value={star}>
                    {star} Star{star > 1 && "s"}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comment
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                rows="4"
                placeholder="Write your feedback here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowReviewModal(false);
                  setSelectedOrder(null);
                  setRating(0);
                  setComment("");
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rating || !comment) {
                    toast.error("Please provide both rating and comment.");
                    return;
                  }
                  reviewMutation.mutate({
                    orderId: selectedOrder?._id,
                    rating,
                    comment,
                  });
                }}
                disabled={reviewMutation.isLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {reviewMutation.isLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
}

export default Orders;
